/**
 * Polls /api/geo-config-revision and emits geo-scoped refresh events ONLY when
 * an admin change matches this visitor's session geo (country, province, city, barrio).
 */

import { useEffect, useRef } from "react";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  applyRevisionSeen,
  buildSessionScopeKeys,
  dispatchGeoRevisionEvent,
  fetchGeoConfigRevision,
  filterRevisionForSession,
  findBumpedSessionScopes,
  GEO_CONFIG_REVISION_LS_KEY,
  kindsFromScopes,
  readSeenRevisions,
  sessionShouldPollGeoRevision,
  type GeoRevisionEventDetail,
} from "@/lib/geoConfigRevision";

// Gap entre polls de /api/geo-config-revision.
// Tras admin save el KV ya está fresco; el visitante en sesión necesita detectar
// el bump de scope para re-fetch /api/cta-flags sin reload.
// Fast window: 8s los primeros 2 min (post-mount / volver a pestaña).
// Luego 30s — balance spam vs “publicado ya” para visitantes abiertos.
const FAST_POLL_GAP_MS = 8_000;
const FAST_POLL_WINDOW_MS = 120_000;
const SLOW_POLL_GAP_MS = 30_000;

function processRevisionPayload(
  serverScopes: Record<string, number>,
  sessionKeys: string[],
): GeoRevisionEventDetail | null {
  const lastSeen = readSeenRevisions();
  const bumped = findBumpedSessionScopes(sessionKeys, serverScopes, lastSeen);
  // Registrar TODAS las keys de la sesión (no solo las bumpeadas): inicializa
  // el baseline de la primera visita — el próximo poll compara contra esto en
  // vez de tratar la key como "nunca vista" para siempre.
  applyRevisionSeen(serverScopes, sessionKeys);
  if (bumped.length === 0) return null;
  return { bumpedScopes: bumped, kinds: kindsFromScopes(bumped) };
}

/**
 * Mount once near app root. Coordinates session-scoped refresh signals.
 */
export function useGeoScopedConfigRefresh(): void {
  const sessionKeysRef = useRef<string[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Admin shell no necesita poll de revisión geo — reduce ERR_HTTP2_PROTOCOL_ERROR
    // al competir con /api/admin/* en la misma conexión www.
    if (isAdminShellPath(window.location.pathname)) return;

    const cfg = getServerTemplateConfig();
    sessionKeysRef.current = buildSessionScopeKeys(cfg);
    if (!sessionShouldPollGeoRevision(cfg)) return;

    let cancelled = false;
    const mountedAt = Date.now();

    const poll = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const payload = await fetchGeoConfigRevision();
      if (cancelled || !payload) return;
      const detail = processRevisionPayload(payload.scopes, sessionKeysRef.current);
      if (detail) dispatchGeoRevisionEvent(detail);
    };

    let lastPollAt = 0;
    const currentGapMs = () =>
      Date.now() - mountedAt < FAST_POLL_WINDOW_MS ? FAST_POLL_GAP_MS : SLOW_POLL_GAP_MS;

    const pollIfStale = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastPollAt < currentGapMs()) return;
      lastPollAt = now;
      void poll();
    };

    pollIfStale();
    // Interval visible: detecta bump de scope tras admin save sin esperar focus.
    const intervalId = window.setInterval(pollIfStale, FAST_POLL_GAP_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") pollIfStale();
    };
    const onFocus = () => pollIfStale();

    const onStorage = (e: StorageEvent) => {
      if (cancelled || e.key !== GEO_CONFIG_REVISION_LS_KEY || !e.newValue) return;
      try {
        const raw = JSON.parse(e.newValue) as GeoRevisionEventDetail;
        if (!raw?.bumpedScopes?.length) return;
        const filtered = filterRevisionForSession(raw, sessionKeysRef.current);
        if (!filtered) return;
        const now = Date.now();
        const scopes: Record<string, number> = {};
        for (const k of filtered.bumpedScopes) scopes[k] = now;
        applyRevisionSeen(scopes, filtered.bumpedScopes);
        dispatchGeoRevisionEvent(filtered);
      } catch {
        /* ignore malformed */
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
}